import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import '../AddNewCourse/AddNewCourse.css';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import InstaTopbar from '../../pages/InstaTopbar/InstaTopbar';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import { FaPlus, FaFileVideo, FaRegFileVideo, FaClock, FaStar } from 'react-icons/fa'; // Add this import at the top
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  faUpload,
  faImage,
  faFileAlt,
  faWeightHanging,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Add this helper function at the top (after imports)
const countWords = (str) => str.trim().split(/\s+/).filter(Boolean).length;

const MAX_INTRO_VIDEO_SECONDS = 5 * 60;

async function getVideoMetadata(file) {
  const objectUrl = URL.createObjectURL(file);

  return await new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = objectUrl;

    const cleanup = () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch (_e) {
        // ignore
      }
      video.removeAttribute('src');
      try {
        video.load();
      } catch (_e) {
        // ignore
      }
    };

    video.onerror = () => {
      cleanup();
      reject(new Error('Failed to load video metadata.'));
    };

    video.onloadedmetadata = () => {
      // Some browsers may report Infinity/NaN until a seek occurs.
      if (!Number.isFinite(video.duration) || video.duration === Infinity) {
        video.currentTime = 1e101;
        video.ontimeupdate = () => {
          video.ontimeupdate = null;
          const duration = video.duration;
          const height = video.videoHeight;
          cleanup();
          if (!Number.isFinite(duration) || duration <= 0) {
            reject(new Error('Unable to determine video duration.'));
            return;
          }
          resolve({ duration, height });
        };
        return;
      }

      const duration = video.duration;
      const height = video.videoHeight;
      cleanup();
      resolve({ duration, height });
    };
  });
}

const AddNewCourse = () => {
  useBlockCheck('instructor'); // 🔥 Prevents blocked users from accessing this page
  const { token } = useAuth();

  const [description, setDescription] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  // const [videoFilePath, setVideoFilePath] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  // const [message, setMessage] = useState('');
  const [remainingCharacters, setRemainingCharacters] = useState(150);
  const [videoPreview, setVideoPreview] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [youLearner, setYouLearner] = useState([]);
  const [detailedDescription, setDetailedDescription] = useState('');
  const [categories, setCategories] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [courseName, setCourseName] = useState('');
  const [language, setLanguage] = useState(''); // Add language state
  const [customLanguage, setCustomLanguage] = useState(''); // State for custom language
  const [targetAudience, setTargetAudience] = useState([]); // Add state
  const [dragActive, setDragActive] = useState(false);
  const videoInputRef = useRef(null); // Ref for video file input
  const [videoDuration, setVideoDuration] = useState(null);
  const [videoQuality, setVideoQuality] = useState(null);
  const [isVideoValidating, setIsVideoValidating] = useState(false);
  const [loading, setLoading] = useState(false); // Add loading state
  const uploadDivRef = useRef(null); // Ref for the upload container div

  const navigate = useNavigate();

  const handleAddRequirement = (index = null) => {
    if (index === null) {
      // Add at the end if no index provided
      setRequirements([...requirements, '']);
    } else {
      // Add after the specified index
      const updated = [...requirements];
      updated.splice(index + 1, 0, '');
      setRequirements(updated);
    }
  };

  const handleRequirementChange = (index, value) => {
    const updatedRequirements = [...requirements];
    updatedRequirements[index] = value;
    setRequirements(updatedRequirements);
  };

  const handleRemoveRequirement = (index) => {
    const updatedRequirements = [...requirements];
    updatedRequirements.splice(index, 1);
    setRequirements(updatedRequirements);
  };

  const handleAddYouLearner = (index = null) => {
    if (index === null) {
      // Add at the end if no index provided
      setYouLearner([...youLearner, '']);
    } else {
      // Add after the specified index
      const updated = [...youLearner];
      updated.splice(index + 1, 0, '');
      setYouLearner(updated);
    }
  };

  // Update handleYouLearnerChange to enforce the word limit
  const handleYouLearnerChange = (index, value) => {
    if (countWords(value) <= 20) {
      const updatedYouLearner = [...youLearner];
      updatedYouLearner[index] = value;
      setYouLearner(updatedYouLearner);
    }
  };

  const handleAddTargetAudience = (index = null) => {
    if (index === null) {
      // Add at the end if no index provided
      setTargetAudience([...targetAudience, '']);
    } else {
      // Add after the specified index
      const updated = [...targetAudience];
      updated.splice(index + 1, 0, '');
      setTargetAudience(updated);
    }
  };

  const handleTargetAudienceChange = (index, value) => {
    const updated = [...targetAudience];
    updated[index] = value;
    setTargetAudience(updated);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${api.getAllCategories}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, [token]);

  // Clean up any created object URLs for the video preview
  useEffect(() => {
    return () => {
      if (videoPreview) URL.revokeObjectURL(videoPreview);
    };
  }, [videoPreview]);

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    const selectedCategoryData = categories.find((cat) => cat._id === categoryId);
    setSubcategories(selectedCategoryData?.subcategories || []);
    setSelectedSubcategory('');
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (type === 'video' && file) {
      // Check video file type
      const validTypes = ['video/mp4', 'video/webm', 'video/avi'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid video file (MP4, WebM, AVI).');
        return;
      }

      // Check video file size (500MB limit for introduction video)
      const MAX_INTRO_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
      if (file.size > MAX_INTRO_VIDEO_SIZE) {
        toast.error('Introduction video size exceeds the maximum limit of 500MB. Please compress the video or use a smaller file.');
        return;
      }

      // Generate video preview URL
      const videoUrl = URL.createObjectURL(file);
      setVideoPreview(videoUrl);

      // Validate duration + extract quality from metadata
      setIsVideoValidating(true);
      setVideoFile(null);
      setVideoDuration(null);
      setVideoQuality(null);

      try {
        const { duration, height } = await getVideoMetadata(file);

        if (duration > MAX_INTRO_VIDEO_SECONDS) {
          toast.error('Introduction video must be 5 minutes or less.');
          setVideoPreview(null);
          setVideoFile(null);
          setVideoDuration(null);
          setVideoQuality(null);
          if (videoInputRef.current) videoInputRef.current.value = '';
          return;
        }

        const quality = height >= 720 ? '720p or higher' : 'Below 720p';
        setVideoDuration(duration);
        setVideoQuality(quality);
        setVideoFile(file);
      } catch (_err) {
        toast.error('Could not verify video duration. Please try another video.');
        setVideoPreview(null);
        setVideoFile(null);
        setVideoDuration(null);
        setVideoQuality(null);
        if (videoInputRef.current) videoInputRef.current.value = '';
      } finally {
        setIsVideoValidating(false);
      }
    } else if (type === 'image' && file) {
      // Check image file type
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validImageTypes.includes(file.type)) {
        toast.error('Please upload a valid image file (JPEG, PNG, GIF, WebP).');
        return;
      }

      // Check image file size (10MB limit)
      const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error('Image file size exceeds the maximum limit of 10MB. Please compress the image or use a smaller file.');
        return;
      }

      // Generate image preview URL
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
      setImageFile(file);
    } else {
      toast.error(`Please upload a valid ${type} file.`);
    }
  };

  // const handleLanguageChange = (e) => {
  //   const selectedValue = e.target.value;
  //   setLanguage(selectedValue);
  //   if (selectedValue !== 'Other') {
  //     setCustomLanguage(''); // Reset custom language if "Other" is not selected
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true); // Start loading
    setTimeout(async () => {
      const finalLanguage = language === 'Other' ? customLanguage : language;

      if (!courseName || !description || !videoFile || !imageFile || !selectedCategory || !selectedSubcategory || !finalLanguage) {
        Swal.fire('Error', 'Please provide all the required details.', 'error');
        return;
      }

      const formData = new FormData();
      formData.append('title', courseName);
      formData.append('description', description);
      formData.append('category', selectedCategory);
      formData.append('subcategory', selectedSubcategory);
      formData.append('language', finalLanguage); // Use final language
      formData.append('video', videoFile);
      formData.append('image', imageFile);
      formData.append('detailedDescription', detailedDescription);
      formData.append('requirements', JSON.stringify(requirements));
      formData.append('youLearner', JSON.stringify(youLearner));
      formData.append('targetAudience', JSON.stringify(targetAudience)); // Append target audience

      try {
        // Calculate total size for debugging
        const videoSizeMB = videoFile ? (videoFile.size / 1024 / 1024).toFixed(2) : 0;
        const imageSizeMB = imageFile ? (imageFile.size / 1024 / 1024).toFixed(2) : 0;
        const totalSize = (videoFile?.size || 0) + (imageFile?.size || 0);
        const totalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
        
        console.log('📤 Uploading course:', {
          videoSize: videoFile ? `${videoSizeMB} MB` : 'N/A',
          imageSize: imageFile ? `${imageSizeMB} MB` : 'N/A',
          totalSize: `${totalSizeMB} MB`,
        });

        // Warn if video is very large
        if (videoFile && videoFile.size > 500 * 1024 * 1024) { // > 500MB
          console.warn('⚠️ Video file is large (>500MB). Upload may take time or fail if server limits are too low.');
        }

        const response = await axios.post(api.addCourse, formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
          maxContentLength: 1024 * 1024 * 1024 * 2, // 2GB max content length
          maxBodyLength: 1024 * 1024 * 1024 * 2, // 2GB max body length
          timeout: 300000, // 5 minutes timeout for large file uploads
        });

        // Use draft._id as the created course identifier
        const createdDraftId = response.data.draft && response.data.draft._id;
        if (!createdDraftId) {
          Swal.fire('Error', 'Draft ID not found in the response.', 'error');
          return;
        }

        Swal.fire({
          title: 'Success',
          text: response.data.message || 'Course draft added successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then((_result) => {
          if (createdDraftId) {
            navigate(`/instructor-course-details/${createdDraftId}`);
          }
        });

        // Reset form
        setCourseName('');
        setDescription('');
        setVideoFile(null);
        setImageFile(null);
        setSelectedCategory('');
        setSubcategories([]);
        setSelectedSubcategory('');
        setDetailedDescription('');
        setRequirements([]);
        setYouLearner([]);
        setLanguage(''); setCustomLanguage(''); setTargetAudience([]); setRemainingCharacters(150);
        setCurrentStep(1);
        setLoading(false); // Stop loading on success
      } catch (error) {
        console.error('Error adding course:', error);
        
        // Calculate file sizes for error message
        const videoSizeMB = videoFile ? (videoFile.size / 1024 / 1024).toFixed(2) : 0;
        const imageSizeMB = imageFile ? (imageFile.size / 1024 / 1024).toFixed(2) : 0;
        const totalSize = (videoFile?.size || 0) + (imageFile?.size || 0);
        const totalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
        
        // Handle specific error cases
        if (error.response?.status === 413) {
          let errorMessage = `The upload failed because the files are too large.<br/><br/>`;
          errorMessage += `<strong>Your file sizes:</strong><br/>`;
          errorMessage += `• Video: ${videoFile ? `${videoSizeMB} MB` : 'Not uploaded'}<br/>`;
          errorMessage += `• Image: ${imageFile ? `${imageSizeMB} MB` : 'Not uploaded'}<br/>`;
          errorMessage += `• Total: ${totalSizeMB} MB<br/><br/>`;
          
          if (videoFile && videoFile.size > 1024 * 1024 * 1024) {
            errorMessage += `<strong style="color: red;">⚠️ Your video file (${videoSizeMB} MB) exceeds the 1GB limit!</strong><br/>`;
            errorMessage += `Please compress or use a smaller video file.<br/><br/>`;
          } else if (totalSize > 100 * 1024 * 1024) {
            errorMessage += `<strong>Possible issues:</strong><br/>`;
            errorMessage += `• The server may have a limit lower than expected<br/>`;
            errorMessage += `• Your reverse proxy (Nginx) may need configuration<br/>`;
            errorMessage += `• Try compressing the video file further<br/>`;
          } else {
            errorMessage += `<strong>This might be a server configuration issue.</strong><br/>`;
            errorMessage += `Please contact support with these file sizes.`;
          }
          
          Swal.fire({
            icon: 'error',
            title: 'Upload Failed - File Too Large',
            html: errorMessage,
          });
        } else if (error.response?.status === 400) {
          const errorMessage = error.response?.data?.message || 'Invalid file format or missing required fields.';
          Swal.fire('Error', errorMessage, 'error');
        } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          Swal.fire('Error', 'Upload timeout. The files may be too large. Please try with smaller files.', 'error');
        } else {
          const errorMessage = error.response?.data?.message || error.message || 'An error occurred while adding the course.';
          Swal.fire('Error', errorMessage, 'error');
        }
        setLoading(false); // Stop loading on error
      }
    }, 50); // wait 50ms to ensure state is flushed
  };

  const handleCourseNameChange = (e) => {
    const value = e.target.value;
    setCourseName(value);
    setRemainingCharacters(150 - value.length);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload({ target: { files: [file] } }, 'video');
  };

  // const handleVideoContainerClick = (e) => {
  //   // Prevent double trigger if label or input is clicked
  //   if (
  //     e.target.classList.contains('video-upload-label') ||
  //     e.target.tagName === 'INPUT'
  //   ) {
  //     return;
  //   }
  //   if (videoInputRef.current) {
  //     videoInputRef.current.click();
  //   }
  // };

  // Add keyboard handler for Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // Check if current step can proceed
      const canProceed =
        (currentStep === 1 && courseName) ||
        (currentStep === 2 && description) ||
        (currentStep === 3 && detailedDescription) ||
        (currentStep === 4 && selectedCategory) ||
        (currentStep === 5 && (subcategories.length === 0 || selectedSubcategory)) ||
        (currentStep === 6 && videoFile) ||
        (currentStep === 7 && imageFile) ||
        (currentStep === 8 && requirements.length > 0 && requirements.some(r => r.trim())) ||
        (currentStep === 9 && youLearner.length > 0 && youLearner.some(l => l.trim())) ||
        (currentStep === 10 && language && (language !== 'Other' || customLanguage)) ||
        (currentStep === 11 && targetAudience.length > 0 && targetAudience.some(a => a.trim()));

      if (canProceed) {
        if (currentStep < 11) {
          setCurrentStep(currentStep + 1);
        } else {
          // Submit the form
          handleSubmit(e);
        }
      }
    }
  };

  const stepGuidance = {
    1: 'Choose a clear, learner-friendly title. Mention the topic and expected outcome in simple words.',
    2: 'Write a short overview that explains what learners will gain after completing this course.',
    3: 'Add detailed context so learners understand the depth, structure, and value of your course.',
    4: 'Pick the closest category so learners can discover your course more easily.',
    5: 'Select a precise subcategory to improve relevance and search visibility.',
    6: 'Upload a short, clear introduction video that builds trust and sets expectations.',
    7: 'Use a clean and relevant thumbnail image so learners can identify your course quickly.',
    8: 'List only practical prerequisites that help learners prepare before starting.',
    9: 'Write measurable learning outcomes so learners know exactly what they will achieve.',
    10: 'Choose the main teaching language to improve learner comfort and accessibility.',
    11: 'Describe the ideal audience clearly so the right learners enroll with confidence.',
  };

  const currentStepGuidance = stepGuidance[currentStep] || '';

  const renderStepContent = () => {
    switch (currentStep) {
    case 1:
      return (
        <div>
          <h1 className="add-course-headings">Course Title</h1>
          <p className="add-course-p">Please provide a concise and descriptive name for your course.</p>
          <div>
            <input
              type="text"
              placeholder="Course Name (Only 150 characters)"
              value={courseName}
              onChange={handleCourseNameChange}
              onKeyDown={handleKeyDown}
              maxLength={150}
              required
              className="anc-input"
            />
          </div>
          <div className="character-limit-container">
            <p className="character-limit">{remainingCharacters}</p>
          </div>
        </div>
      );
    case 2:
      return (
        <div>
          <h1 className="add-course-headings">Course Description</h1>
          <p className="add-course-p">Provide a detailed overview of the course objectives, topics, and outcomes of your course.</p>

          <div className="textarea-container">
            <textarea
              placeholder="Course Description (Max 20 words)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              required
              className="anc-textarea"
            ></textarea>
            <small className="word-count">{countWords(description)} / 20 </small>
          </div>
        </div>

      );
    case 3:
      return (
        <div>
          <h1>Detailed Description of this course</h1>
          <textarea
            value={detailedDescription}
            onChange={(e) => setDetailedDescription(e.target.value)}
            onKeyDown={handleKeyDown}
            className="anc-textarea"
          />
        </div>
      );
    case 4:
      return (
        <div>
          <h1 className="add-course-headings">Course Category</h1>
          <p className="add-course-p">Assign to a relevant category.</p>
          <select value={selectedCategory}
            onChange={handleCategoryChange}
            onKeyDown={handleKeyDown}
            required
            className="anc-select">
            <option value="" disabled>
                Select Category
            </option>
            {categories.map((category) => (
              <option key={category._id} value={category._id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      );
    case 5:
      return (
        <div>
          <h1 className="add-course-headings">Subcategory</h1>
          <p className="add-course-p">Choose the subcategory that further defines your course.</p>
          {subcategories.length > 0 && (
            <select
              value={selectedSubcategory}
              onChange={(e) => setSelectedSubcategory(e.target.value)}
              onKeyDown={handleKeyDown}
              required
              className="anc-select"
            >
              <option value="" disabled>
                  Select Subcategory
              </option>
              {subcategories.map((subcategory) => (
                <option key={subcategory._id} value={subcategory._id}>
                  {subcategory.name}
                </option>
              ))}
            </select>
          )}
        </div>
      );
    case 6:
      return (
        <div>
          <h1 className="add-course-headings">Course Introduction Video</h1>
          <p className="add-course-p">This video will give learners an overview of your course.</p>
          <div
            className={`video-upload-container${dragActive ? ' drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => videoInputRef.current && videoInputRef.current.click()}
            style={{ cursor: 'pointer' }}
            tabIndex={0} // <-- Make it focusable
            onKeyDown={handleKeyDown} // <-- Listen for Enter
            ref={uploadDivRef} // <-- (optional) for programmatic focus
          >
            <input
              id="video-upload"
              type="file"
              accept="video/*"
              onChange={(e) => handleFileUpload(e, 'video')}
              className="video-input"
              style={{ display: 'none' }}
              required
              ref={videoInputRef}
            />
            <div className="upload-instructions">
              <FontAwesomeIcon icon={faUpload} className="upload-icon" />
              <p>Drag and drop your video here or click to browse</p>
              <p>Supported formats: MP4, WebM, AVI</p>
              <p className="video-duration-warning"><strong>Maximum duration: 5 minutes</strong></p>
              <p className="video-duration-warning"><strong>Maximum file size: 500MB</strong></p>
            </div>
          </div>
          {videoPreview && (
            <div className="preview-container">
              <h2>Video Preview:</h2>
              <video key={videoPreview} controls>
                <source src={videoPreview} type={videoFile?.type || 'video/mp4'} />
                  Your browser does not support the video tag.
              </video>

              {isVideoValidating && (
                <p style={{ marginTop: 8, fontWeight: 600 }}>
                  Validating video duration…
                </p>
              )}

              <div className="file-info">
                <div className="file-info-grid">
                  <div className="file-info-item">
                    <FaFileVideo />
                    <p>Type: {videoFile && videoFile.type.split('/')[1].toUpperCase()}</p>
                  </div>
                  <div className="file-info-item">
                    <FaRegFileVideo />
                    <p>Size: {videoFile && (videoFile.size / 1024 / 1024).toFixed(2) + ' MB'}</p>
                  </div>
                  <div className="file-info-item">
                    <FaStar />
                    <p>Quality: {videoQuality || 'Loading...'}</p>
                  </div>
                  <div className="file-info-item">
                    <FaClock />
                    <p>Duration: {videoDuration ? `${Math.floor(videoDuration / 60)}:${Math.floor(videoDuration % 60).toString().padStart(2, '0')}` : 'Loading...'}</p>
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>
      );
    case 7:
      return (
        <div>
          <h1 className="add-course-headings">Course Thumbnail</h1>
          <p className="add-course-p">The thumbnail will represent your course visually.</p>
          <div className="file-upload-section">
            <label htmlFor="image-upload" className="file-upload-label">
              <FontAwesomeIcon icon={faImage} style={{ color: '#9142e1', marginRight: '8px' }} />
                Upload Course Thumbnail Image
            </label>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'image')}
              required
              className="file-upload-input"
            />
            {imagePreview && (
              <div className="preview-container">
                <h2>Image Preview:</h2>
                <img src={imagePreview} alt="Course Thumbnail" className="thumbnail-preview" />
                <div className="img-file-info">
                  <p className="file-info-item">
                    <FontAwesomeIcon icon={faFileAlt} className="file-info-icon" />
                      Type: {imageFile && imageFile.type.split('/')[1].toUpperCase()}
                  </p>
                  <p className="file-info-item">
                    <FontAwesomeIcon icon={faWeightHanging} className="file-info-icon" />
                      Size: {imageFile && (imageFile.size / 1024 / 1024).toFixed(2) + ' MB'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    case 8:
      return (
        <div>
          <h1>Requirements for this Course</h1>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {requirements.length === 0 && (
              <button
                type="button"
                onClick={() => handleAddRequirement()}
                className="add-first-item-btn"
              >
                + Add Requirement
              </button>
            )}
            {requirements.map((req, index) => (
              <div key={index} className="field-row">
                <input
                  type="text"
                  value={req}
                  onChange={(e) => handleRequirementChange(index, e.target.value)}
                  placeholder="Enter a requirement (e.g., Basic Python knowledge)"
                  className="anc-input field-input"
                />
                <button
                  type="button"
                  onClick={() => handleAddRequirement(index)}
                  className="field-add-btn"
                  title="Add requirement after this"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveRequirement(index)}
                  className="field-remove-btn"
                  title="Remove this requirement"
                >
                  &minus;
                </button>
              </div>
            ))}
          </div>
        </div>
      );
    case 9:
      return (
        <div>
          <h1>What will students learn in your course?</h1>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {youLearner.length === 0 && (
              <button
                type="button"
                onClick={() => handleAddYouLearner()}
                className="add-first-item-btn"
              >
                + Add Learning Outcome
              </button>
            )}
            {youLearner.map((item, index) => (
              <div key={index} className="field-row">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => handleYouLearnerChange(index, e.target.value)}
                  placeholder="Enter learning outcome (max 20 words)"
                  className="anc-input field-input"
                />
                <button
                  type="button"
                  onClick={() => handleAddYouLearner(index)}
                  className="field-add-btn"
                  title="Add learning outcome after this"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const updated = [...youLearner];
                    updated.splice(index, 1);
                    setYouLearner(updated);
                  }}
                  className="field-remove-btn"
                  title="Remove this learning outcome"
                >
                  &minus;
                </button>
              </div>
            ))}
          </div>
        </div>
      );
    case 10:
      return (
        <div>
          <h1>Language</h1>
          <p className="add-course-p">Select the language of your course.</p>
          <select
            value={language}
            onChange={(e) => {
              const selectedValue = e.target.value;
              setLanguage(selectedValue);
              if (selectedValue !== 'Other') {
                setCustomLanguage(''); // Reset custom language if "Other" is not selected
              }
            }}
            onKeyDown={handleKeyDown}
            required
            className="anc-select"
          >
            <option value="" disabled>
                Select Language
            </option>
            <option value="English">English</option>
            <option value="Kannada">Kannada</option>
            <option value="Spanish">Spanish</option>
            <option value="French">French</option>
            <option value="German">German</option>
            <option value="Chinese">Chinese</option>
            <option value="Japanese">Japanese</option>
            <option value="Hindi">Hindi</option>
            <option value="Arabic">Arabic</option>
            <option value="Other">Other</option>
          </select>

          {language === 'Other' && (
            <div>
              <label htmlFor="custom-language">Enter Custom Language:</label>
              <input
                id="custom-language"
                type="text"
                value={customLanguage}
                onChange={(e) => setCustomLanguage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your language"
                required
                className="anc-input"
              />
            </div>
          )}
        </div>
      );
    case 11:
      return (
        <div>
          <h1>To whom this course for?</h1>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {targetAudience.length === 0 && (
              <button
                type="button"
                onClick={() => handleAddTargetAudience()}
                className="add-first-item-btn"
              >
                + Add Target Audience
              </button>
            )}
            {targetAudience.map((aud, index) => (
              <div key={index} className="field-row">
                <input
                  type="text"
                  value={aud}
                  onChange={(e) => handleTargetAudienceChange(index, e.target.value)}
                  placeholder="Enter target audience"
                  className="anc-input field-input"
                />
                <button
                  type="button"
                  onClick={() => handleAddTargetAudience(index)}
                  className="field-add-btn"
                  title="Add target audience after this"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const updated = [...targetAudience];
                    updated.splice(index, 1);
                    setTargetAudience(updated);
                  }}
                  className="field-remove-btn"
                  title="Remove this target audience"
                >
                  &minus;
                </button>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
    }
  };

  return (
    <div className="add-course-page-wrapper">
      <InstaTopbar />
      <h1 className="add-course-h1">Share Your Knowledge - Add a Course</h1>
      <div className="add-page-container">
        <form className="form-container" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
          {currentStep === 1 && (
            <div className="add-course-title-banner" role="status" aria-live="polite">
              <strong>Title Tip for Instructors:</strong> Please provide a polite, specific, and meaningful title that clearly tells learners what this course offers.
            </div>
          )}

          <div className="add-course-step-banner" role="status" aria-live="polite">
            <p className="add-course-step-banner-title">Step {currentStep} of 11 Guidance</p>
            <p className="add-course-step-banner-text">{currentStepGuidance}</p>
          </div>

          {renderStepContent()}
          <div className="form-navigation">
            {currentStep > 1 && (
              <button
                className="add-course-back-btn"
                type="button"
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                Back
              </button>
            )}
            {currentStep === 1 && (
              <button
                className="add-course-back-btn"
                type="button"
                onClick={() => navigate('/my-courses')}
                style={{ marginLeft: '10px' }}
              >
                Go to My Courses
              </button>
            )}
            {currentStep < 11 ? (
              <button
                className="add-course-next-btn"
                type="button"
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={
                  (currentStep === 1 && !courseName) ||
                  (currentStep === 2 && !description) ||
                  (currentStep === 3 && !detailedDescription) ||
                  (currentStep === 4 && !selectedCategory) ||
                  (currentStep === 5 && subcategories.length > 0 && !selectedSubcategory) ||
                  (currentStep === 6 && (isVideoValidating || !videoFile)) ||
                  (currentStep === 7 && !imageFile) ||
                  (currentStep === 8 && (!requirements.length || requirements.every(r => !r.trim()))) ||
                  (currentStep === 9 && (!youLearner.length || youLearner.every(l => !l.trim()))) ||
                  (currentStep === 10 && (!language || (language === 'Other' && !customLanguage)))
                }
              >
                Next
              </button>
            ) : (
              <button className="add-submit-button" type="submit" disabled={loading || targetAudience.length === 0 || targetAudience.every(a => !a.trim())}>
                {loading ? (
                  <span className="spinner" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }}>
                    <svg width="22" height="22" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg" stroke="#9142e1">
                      <g fill="none" fillRule="evenodd" strokeWidth="4">
                        <circle cx="22" cy="22" r="18" strokeOpacity=".5" />
                        <path d="M40 22c0-9.94-8.06-18-18-18">
                          <animateTransform attributeName="transform" type="rotate" from="0 22 22" to="360 22 22" dur="1s" repeatCount="indefinite" />
                        </path>
                      </g>
                    </svg>
                  </span>
                ) : null}
                {loading ? 'Adding...' : 'Add Course'}
              </button>
            )}
          </div>
        </form>
        <ToastContainer />
      </div>
    </div>
  );
};

export default AddNewCourse;